import { Dropdown, List, Stack, Strong, Text } from 'braid-design-system';

import { type Role, roleDisplayNames } from '#src/types/users.ts';

const ReadonlyUserDescription = () => (
  <Stack space="medium">
    <Text>
      Grants <Strong>read only</Strong> permissions for resources in the
      Developer Dashboard. Users will <Strong>not</Strong> have access to the
      Credentials, GraphQL Explorer and Users pages.
    </Text>

    <Text>Users can expect to do some of the following:</Text>

    <List>
      <Text tone="info">View posted job ad information</Text>
      <Text tone="info">View webhook events</Text>
      <Text tone="info">
        Search for SEEK hirers that you have an active relationship with
      </Text>
    </List>
  </Stack>
);

const UserDescription = () => (
  <Stack space="medium">
    <Text>
      Grants <Strong>write</Strong> permissions for the{' '}
      <Strong>Webhooks</Strong> and <Strong>Notifications</Strong> pages in the
      Developer Dashboard as well as any permissions associated with the read
      only role. Users will <Strong>not</Strong> have access to the Credentials,
      GraphQL Explorer and Users pages.
    </Text>

    <Text>Users can expect to do some of the following:</Text>

    <List>
      <Text tone="info">
        Create, update, delete and replay webhook subscriptions
      </Text>
      <Text tone="info">Create and update notifications</Text>
    </List>
  </Stack>
);

const AdminDescription = () => (
  <Stack space="medium">
    <Text>
      Grants <Strong>privileged</Strong> permissions for resources in the
      Developer Dashboard as well as any permissions associated with the
      previous roles.
    </Text>

    <Text>This will allow users to:</Text>

    <List>
      <Text tone="info">View, create and rotate client credentials</Text>
      <Text tone="info">
        Create, update and delete existing Developer Dashboard users
      </Text>
      <Text tone="info">
        Validate queries and mutations via the GraphQL Explorer with your
        playground credentials
      </Text>
    </List>
  </Stack>
);

const roleDescriptions: Record<Role, () => React.JSX.Element> = {
  'read-only': ReadonlyUserDescription,
  edit: UserDescription,
  admin: AdminDescription,
};

export const RoleSelector = ({
  value,
  onBlur,
  onChange,
}: {
  value: Role;
  onChange: (value: Role) => void;
  onBlur?: () => void;
}) => {
  const RoleDescription = roleDescriptions[value];

  return (
    <Stack space="medium">
      <Dropdown
        id="role"
        label="Role"
        value={value}
        onBlur={onBlur}
        onChange={(e) => onChange(e.currentTarget.value as Role)} // The dropdown only contains `Role` values
      >
        {Object.entries(roleDisplayNames).map(([val, roleName]) => (
          <option key={val} value={val}>
            {roleName}
          </option>
        ))}
      </Dropdown>
      <RoleDescription />
    </Stack>
  );
};
